import {
  MultiPolygon2,
  NumberResult,
  Point2,
  Polyline2,
  ResultType,
} from '../../geometric-types';

const EPSILON = 1e-9;

function areSamePoint(a: Point2, b: Point2): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

function uniqueVerticesForClosedRing(ring: Polyline2): Polyline2 {
  // Remove duplicated closing point if present
  let vertices =
    ring.length >= 2 && areSamePoint(ring[0], ring[ring.length - 1])
      ? ring.slice(0, ring.length - 1)
      : ring;

  // Deduplicate consecutive identical vertices inside the ring
  const deduped: Polyline2 = [];
  for (let i = 0; i < vertices.length; i++) {
    const current = vertices[i];
    const prev = deduped.length ? deduped[deduped.length - 1] : undefined;
    if (!prev || !areSamePoint(prev, current)) {
      deduped.push(current);
    }
  }
  return deduped;
}

function countCornersInRing(ring: Polyline2): number {
  // Need at least 4 points for a closed triangle ring (including duplicate)
  if (ring.length < 4) return 0;

  const vertices = uniqueVerticesForClosedRing(ring);
  if (vertices.length < 3) return 0;

  let count = 0;
  const n = vertices.length;
  for (let i = 0; i < n; i++) {
    const prev = vertices[(i - 1 + n) % n];
    const curr = vertices[i];
    const next = vertices[(i + 1) % n];

    const v1x = curr[0] - prev[0];
    const v1y = curr[1] - prev[1];
    const v2x = next[0] - curr[0];
    const v2y = next[1] - curr[1];

    const cross = v1x * v2y - v1y * v2x;
    const len1 = Math.hypot(v1x, v1y);
    const len2 = Math.hypot(v2x, v2y);

    if (len1 < EPSILON || len2 < EPSILON) continue;
    if (Math.abs(cross) > EPSILON) count += 1;
  }
  return count;
}

export function calculateSheetShapeNumberOfExternalCorners(
  shape: MultiPolygon2
): NumberResult {
  const lines: Polyline2[] = [];

  for (const polygon of shape.polygons) {
    lines.push(polygon.exterior);
  }

  const totalCorners = lines.reduce(
    (sum, ring) => sum + countCornersInRing(ring),
    0
  );

  return {
    type: ResultType.NUMBER,
    value: totalCorners,
  };
}
